// libs
import React, { memo, useState, useEffect } from "react";
import get from "lodash/get";

// global components
import ResponsiveLoader from "shared/components/ResponsiveLoader";
import Button from "shared/components/Button";

// components
import Title from "shared/components/AuthModal/components/Title";
import InputLabel from "shared/components/AuthModal/components/InputLabel";
import Password from "shared/components/AuthModal/components/Password";
import Terms from "shared/components/AuthModal/components/Terms";
import Error from "shared/components/AuthModal/components/Error";
import LinkButton from "shared/components/AuthModal/components/LinkButton";
import Input from "shared/components/AuthModal/components/Input";

// contexts
import { useModalContext } from "shared/components/Modal";
import { useAuthModalContext } from "shared/components/AuthModal/AuthModalContext";

// helpers
import {
    validateEmail,
    validatePassword,
    hasSocialLoginError,
    clearLoginError,
} from "shared/components/AuthModal/helpers";

// api
import { logIn } from "shared/api/auth";

// analytics
import * as Analytics from "shared/components/Header/analytics";

// constants
import { TEXTS, FORM_STATE } from "shared/components/AuthModal/constants";
import { BREAKPOINTS } from "shared/styles/base";
import { ILoginResponse, LOGIN_ERROR_CODE } from "./types";

// styled components
import { LoginForm } from "./styles";

interface ILoginProps {
    formKey: string;
    updateAppUser: (user: any) => void;
}

function Login({ formKey, updateAppUser }: ILoginProps) {
    const [error, setError] = useState("");
    const [email, setEmail] = useState("");
    const [isEmailError, setEmailError] = useState(false);
    const [password, setPassword] = useState("");
    const [isPasswordError, setPasswordError] = useState(false);
    const [inProgress, setProgress] = useState(false);

    const { hide } = useModalContext();
    const { setFormState, onAuthSuccess } = useAuthModalContext();

    useEffect(() => {
        if (hasSocialLoginError()) {
            setEmailError(true);
            setError(TEXTS.LOGIN_USING_EMAIL);
            clearLoginError();
        }
    }, []);

    const onPasswordChange: React.ChangeEventHandler<HTMLInputElement> = e =>
        setPassword(e.target.value);

    const onEmailChange: React.ChangeEventHandler<HTMLInputElement> = e => setEmail(e.target.value);

    const onFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (inProgress) {
            return;
        }

        Analytics.onSignInAttemptClick();
        setEmailError(false);
        setPasswordError(false);

        const { status: emailStatus, error: emailError } = validateEmail(email);
        if (emailStatus === false) {
            // @ts-ignore
            setError(emailError);
            setEmailError(true);
            return;
        }

        const { status: passwordStatus, error: passwordError } = validatePassword(password);
        if (passwordStatus === false) {
            // @ts-ignore
            setError(passwordError);
            setPasswordError(true);
            return;
        }

        setError("");
        setProgress(true);

        try {
            const response: ILoginResponse = await logIn(email, password, formKey);

            if (get(response, "error") === LOGIN_ERROR_CODE.ERROR) {
                setProgress(false);
                setError(get(response, "message", TEXTS.OOPS));
                Analytics.onSignInFailed();
                return;
            }

            const { form_key, cartCount, customerData } = response.response;

            Analytics.onSignInSuccess();
            updateAppUser({ formKey: form_key, cartCount, customerData });
            onAuthSuccess();
            hide();
        } catch (err) {
            hide();
            setProgress(false);
            setError(TEXTS.OOPS);
        }
    };

    return (
        <LoginForm data-cy="login-form" onSubmit={onFormSubmit}>
            <ResponsiveLoader breakpoint={BREAKPOINTS.MOBILE} up>
                <Title>{TEXTS.LOGIN}</Title>
            </ResponsiveLoader>

            <InputLabel>{TEXTS.EMAIL}</InputLabel>
            <Input
                onChange={onEmailChange}
                data-cy="login-email"
                autoComplete="username"
                hasError={isEmailError}
                className="input"
            />
            <InputLabel>{TEXTS.PASSWORD}</InputLabel>
            <Password
                onChange={onPasswordChange}
                dataCy="login-password"
                hasError={isPasswordError}
            />
            <div className="forget-container">
                <LinkButton
                    type="button"
                    onClick={() => setFormState(FORM_STATE.FORGOT_PASSWORD)}
                    data-test="forgot-btn"
                >
                    {TEXTS.FORGOT_PASSWORD}
                </LinkButton>
            </div>
            {error.length > 0 && <Error data-test="fp-error">{error}</Error>}
            <Button data-cy="login-btn" inProgress={inProgress}>
                {TEXTS.LOGIN}
            </Button>
            <Terms data-test="terms" />
        </LoginForm>
    );
}

export default memo(Login);
